// Хук для работы с Wildberries API
import { useState, useCallback } from 'react'
import { wildberriesAPI } from '../api/wildberries'
import { WBProduct, ComplianceCheck } from '../types/wildberries'

export const useWildberries = () => {
  const [products, setProducts] = useState<WBProduct[]>([])
  const [checks, setChecks] = useState<Record<string, ComplianceCheck>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Проверить, что токен по-прежнему рабочий (пинг-запрос)
  const verifyConnection = useCallback(async (): Promise<boolean> => {
    setLoading(true)
    setError(null)
    try {
      return await wildberriesAPI.verifyConnection()
    } catch (err: any) {
      setError(err.message || 'Ошибка проверки подключения')
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  // Загрузить товары
  const fetchProducts = useCallback(async (limit = 50, offset = 0): Promise<WBProduct[]> => {
    setLoading(true)
    setError(null)
    try {
      const { products: fetchedProducts } = await wildberriesAPI.fetchProducts(limit, offset)
      setProducts(fetchedProducts)
      return fetchedProducts
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Ошибка загрузки товаров')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  // Проверить одну карточку
  const checkProduct = useCallback(async (product: WBProduct): Promise<ComplianceCheck | null> => {
    setLoading(true)
    setError(null)
    try {
        const checkData = await wildberriesAPI.checkProduct(
        product.nm_id,
        product.description,
        { brand: product.brand, category: product.category }
        )
        
        console.log('✅ checkProduct: received', checkData);
        
        // ✅ 🔑 КЛЮЧЕВОЙ ФИКС: добавляем nm_id, если его нет в ответе
        const checkWithId = {
        ...checkData,
        nm_id: checkData.nm_id || product.nm_id  // ← берём из продукта, если бэк не вернул
        }
        
        // ✅ Теперь условие сработает
        if (checkWithId.nm_id) {
        setChecks(prev => ({ 
            ...prev, 
            [checkWithId.nm_id]: checkWithId  // ← сохраняем с гарантированным nm_id
        }))
        }
        
        return checkWithId
    } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Ошибка проверки товара')
        return null
    } finally {
        setLoading(false)
    }
    }, [])  // ✅ Пустой массив зависимостей

  // Проверить пакет
  const checkBatch = useCallback(async (limit = 10): Promise<ComplianceCheck[]> => {
    setLoading(true)
    setError(null)
    try {
        const response = await wildberriesAPI.checkProductsBatch(limit)
        const results = response.checked_products || response || []
        
        console.log('✅ checkBatch: received', results.length, 'checks')
        
        // ✅ Функциональное обновление + добавление nm_id
        setChecks(prev => {
        const newChecks = { ...prev }
        results.forEach((check: any) => {
            // ✅ Гарантируем nm_id для каждого чека
            const nmId = check.nm_id || check.product?.nm_id
            if (nmId) {
            newChecks[nmId] = { ...check, nm_id: nmId }
            }
        })
        return newChecks
        })
        
        return results
    } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Ошибка пакетной проверки')
        return []
    } finally {
        setLoading(false)
    }
    }, [])  // ✅ Убрали [checks] из зависимостей

  // Получить результат проверки для товара (из кэша или API)
  const getCheckForProduct = useCallback(async (nm_id: string): Promise<ComplianceCheck | null> => {
    if (checks[nm_id]) {
      return checks[nm_id]
    }
    
    // Попробовать загрузить с сервера
    setLoading(true)
    try {
      const check = await wildberriesAPI.getCheckResult(nm_id)
      if (check) {
        setChecks(prev => ({ ...prev, [nm_id]: check }))
      }
      return check
    } catch {
      return null
    } finally {
      setLoading(false)
    }
  }, [checks])

  // Очистить ошибку
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    products,
    checks,
    setChecks,
    loading,
    error,
    verifyConnection,
    fetchProducts,
    checkProduct,
    checkBatch,
    getCheckForProduct,
    clearError,
  }
}